import guideLogo from '@/assets/images/guide-logo.webp';
import { QUERY_KEYS } from '@/constants';
import type { ITag } from '@/services/workbench';
import { createUserTags, getUserTags } from '@/services/workbench';
import { Button, Input, PureDialog } from '@ai-indeed/idp-ui';
import { useModel, useMutation, useQuery } from '@umijs/max';
import { message } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { IconFont } from '../IconFont';
import { CheckboxGroup } from './components';
import './index.less';
import type { IFormValue } from './type';

export const Guide: FC<{ onFinished: () => void }> = (props) => {
  // props
  const { onFinished } = props;
  // initial state
  const { initialState } = useModel('@@initialState');
  // state
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);
  // ref
  const destroyRef = useRef<(() => void) | null>(null);
  // form
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValue>({
    defaultValues: {
      username: initialState ? initialState.userInfo.username : '',
    },
  });
  // query
  const { data } = useQuery({
    queryKey: [QUERY_KEYS.userTagList],
    queryFn: () =>
      getUserTags().then((res) => {
        const { code, data: tagList, msg } = res;
        if (code !== 0) {
          message.error(msg || '请求错误');
          return;
        }
        const roleOptions = [] as ITag[];
        const industryOptions = [] as ITag[];
        tagList.forEach((tag) => {
          if (tag.type === 0) {
            roleOptions.push(tag);
          } else {
            industryOptions.push(tag);
          }
        });
        return {
          roleOptions,
          industryOptions,
        };
      }),
  });
  // mutation
  const { mutate, isLoading } = useMutation(createUserTags, {
    onSuccess(res) {
      const { code, msg } = res;
      if (code !== 0) {
        message.error(msg || '设置错误');
        return;
      }
      setOpen(false);
      onFinished();
      message.success('设置成功');
    },
    onError() {
      message.error('服务器错误');
    },
  });

  // jsx element
  const slogan = (
    <>
      <p className="text-3xl font-semibold text-main">欢迎来到 IDP·文档审阅</p>
      <p className="text-sm text-secondary-1">
        简单填写个人情况，您将获得个性化推荐
      </p>
    </>
  );
  // methods
  const createMessage = (content: string) => {
    if (destroyRef.current) {
      return;
    }
    destroyRef.current = message.error(content, 2, () => {
      destroyRef.current = null;
    });
  };

  if (step === 0) {
    setTimeout(() => {
      setStep(1);
    }, 1500);
  }
  if (errors.role) {
    createMessage('请选择您的角色');
  } else if (errors.industry) {
    createMessage('请选择所属行业');
  }

  return createPortal(
    <PureDialog
      open={open}
      className={classNames('w-[780px] h-[520px]', { 'h-[630px]': step === 1 })}
    >
      <section className="g-content relative h-full">
        <button
          type="button"
          className="absolute z-10 top-3 right-3 w-8 h-8 p-1 rounded hover:bg-[#EEF2FB]"
          onClick={() => {
            setOpen(false);
            onFinished();
          }}
        >
          <IconFont type="icon-guanbi" />
        </button>
        {step === 0 ? (
          <div className="flex flex-col justify-center h-full items-center gap-3">
            <img
              src={guideLogo}
              className="w-[72px]"
              alt="guide logo"
              loading="lazy"
            />
            {slogan}
          </div>
        ) : (
          <div className="relative h-full">
            <form
              className="h-full"
              onSubmit={handleSubmit((formData) =>
                mutate([formData.role, formData.industry]),
              )}
            >
              <div className="flex flex-col gap-7 h-full px-24 pt-14">
                <div className="flex flex-col gap-3">{slogan}</div>
                <div className="flex-1 flex flex-col gap-6 h-0 pb-[132px] overflow-auto">
                  <section className="flex flex-col gap-4">
                    <label
                      htmlFor="username"
                      className="text-base font-medium text-main"
                    >
                      用户名：
                    </label>
                    <Input
                      disabled
                      id="username"
                      size="large"
                      {...register('username')}
                    />
                  </section>
                  {data && (
                    <>
                      <section className="flex flex-col gap-4">
                        <label
                          htmlFor="field1"
                          className="text-base font-medium text-main"
                        >
                          你的角色：
                        </label>
                        <CheckboxGroup
                          name="role"
                          options={data.roleOptions}
                          control={control}
                        />
                      </section>
                      <section className="flex flex-col gap-4">
                        <label
                          htmlFor="field2"
                          className="text-base font-medium text-main"
                        >
                          所属行业：
                        </label>
                        <CheckboxGroup
                          name="industry"
                          options={data.industryOptions}
                          control={control}
                        />
                      </section>
                    </>
                  )}
                </div>
              </div>
              <footer className="absolute bottom-0 right-0 left-0 flex justify-center h-[120px] pt-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="superLarge"
                  className="w-[200px]"
                  loading={isLoading ? { delay: 200 } : false}
                >
                  {isLoading ? '设置中' : '完成'}
                </Button>
              </footer>
            </form>
          </div>
        )}
      </section>
    </PureDialog>,
    document.querySelector('#portal')!,
  );
};
